<script setup lang="ts">
import { type FontAwesomeIconProps } from '@fortawesome/vue-fontawesome';
import { useWatchErrorHandler } from '@errors/useErrorHandler';
import {
  type SUREbusinessManagementSearchSearchType as ResultType,
  useSearchGet,
} from '@services/overig/orval-api';
import VStatusTag, { type VStatusTagProps } from '@shared/component/VStatusTag/VStatusTag.vue';
import { FontAwesomeIcon } from '@shared/plugin/icons';
import { computed, ref, useTemplateRef, watch } from 'vue';
import VSearch from '@shared/component/VSearch/VSearch.vue';
import VPopover from '@shared/component/VPopover/VPopover.vue';
import VSkeleton from '@shared/component/VSkeleton/VSkeleton.vue';
import { useSessionStorage, useEventListener } from '@vueuse/core';
import { groupBy } from '@tools/helpers';

const term = ref<string>();
const params = computed(() => ({ term: term.value, limit: 5 }));
const popoverRef = useTemplateRef('popover');
const isVisible = computed(() => popoverRef.value?.visible);
const iframeClicked = ref(false);

defineExpose({
  visible: isVisible,
});

const closePopover = () => {
  if (isVisible.value) popoverRef.value?.hide();
};

useEventListener('resize', closePopover);

useEventListener(window, 'blur', () => {
  setTimeout(() => {
    if (document.activeElement?.tagName === 'IFRAME') {
      iframeClicked.value = true;
      closePopover();
    }
  }, 0);
});

const recenteZoekopdrachten = useSessionStorage('OMNISEARCH_RECENTE_OPDRACHTEN', [] as string[]);

watch(term, (t) => {
  if (t && t.length >= 3) recenteZoekopdrachten.value.unshift(t);
  if (recenteZoekopdrachten.value.length > 10) recenteZoekopdrachten.value.pop();
});

const { data, error, isLoading } = useSearchGet(params, {
  query: {
    select: (e) => groupBy(e.result ?? [], (item) => item.type),
    enabled: computed(() => !!term.value && term.value.length >= 3),
  },
});
useWatchErrorHandler(error);

function getStatus(input: string): VStatusTagProps['color'] {
  switch (input) {
    case 'Actief':
    case 'Afgehandeld':
    case 'Akkoord':
    case 'Lopend':
      return 'teal';
    case 'Prospect':
      return 'grey';
    case 'In behandeling':
    case 'Afw. reactie TP':
    case 'Afw. reactie maatsch.':
      return 'orange';
    case 'Beëindigd':
    case 'Geroyeerd':
    case 'Geschorst':
      return 'red';
    default:
      return 'blue';
  }
}

function getIcon(input: ResultType): FontAwesomeIconProps['icon'] {
  switch (input) {
    case 'Relatie':
      return 'far fa-user';
    case 'Schade':
    case 'Schademelding':
      return 'far fa-car-crash';
    case 'Offerte':
      return 'far fa-calculator';
    case 'Aanvraag':
      return 'far fa-file-signature';
    case 'Mutatie':
      return 'far fa-file-circle-check';
    case 'Maatwerk':
      return 'far fa-memo-circle-check';
    case 'Royement':
      return 'far fa-file-xmark';
    case 'Polis':
      return 'far fa-check-double';
    default:
      return 'far fa-file';
  }
}

function open(event: Event) {
  if (isVisible.value === true) {
    popoverRef.value?.hide();
    return;
  }
  popoverRef.value?.show(event);
}

watch(isVisible, (visible) => {
  if (!visible) {
    term.value = undefined;
  }
});
</script>

<template>
  <div
    class="tw-flex tw-cursor-pointer tw-items-center tw-px-4 tw-text-primary-900 tw-no-underline lg:tw-px-6"
    @click="(e) => open(e)"
  >
    <button
      class="tw-flex tw-cursor-pointer tw-items-center tw-gap-1 tw-border-none tw-bg-transparent tw-text-sm tw-text-inherit tw-outline-none"
      type="button"
    >
      <FontAwesomeIcon icon="far fa-search" />
      <span class="tw-hidden xl:tw-display-unset">Zoeken</span>
    </button>

    <VPopover ref="popover" class="tw-max-h-[50vh] tw-overflow-auto">
      <div class="tw-w-[100dvw] xl:tw-w-[588px]">
        <div class="tw-flex tw-flex-col tw-px-2">
          <div class="tw-sticky tw-top-0 tw-bg-white tw-p-2">
            <VSearch
              v-model="term"
              placeholder="Zoeken op relatie-, polis-, schade-, offerte-, KVK-nummer of kenteken"
              class="tw-overflow-hidden tw-rounded-2xl tw-transition-all tw-duration-150 tw-ease-in-out"
            />
          </div>
          <TransitionGroup name="list-complete">
            <div v-for="group in data" :key="group.key" class="tw-p-2">
              <span class="tw-ml-2 tw-flex tw-items-center tw-gap-1 tw-text-sm tw-text-grey-500">
                <FontAwesomeIcon :icon="getIcon(group.key as ResultType)" />
                {{ group.key }}
              </span>
              <div
                v-for="result in group.items"
                :key="result.omschrijving"
                class="tw-group tw-mt-2 tw-rounded tw-p-2 hover:tw-bg-grey-100"
              >
                <a
                  :href="result.url"
                  class="tw-flex tw-flex-row tw-items-center tw-justify-between tw-gap-2 tw-text-grey-700"
                >
                  <div class="tw-flex tw-items-center tw-gap-4">
                    <span
                      :title="result.omschrijving"
                      class="tw-line-clamp-1 tw-break-all tw-font-bold"
                    >
                      {{ result.omschrijving }}
                    </span>
                  </div>
                  <div class="tw-flex tw-items-center">
                    <VStatusTag
                      inverted
                      class="tw-text-nowrap tw-bg-white tw-text-xs"
                      :color="getStatus(result.status)"
                    >
                      {{ result.status }}
                    </VStatusTag>
                  </div>
                </a>
              </div>
            </div>
          </TransitionGroup>
          <div class="tw-p-2" v-if="isLoading">
            <div class="tw-mb-2 tw-w-12">
              <VSkeleton :count="1" height="md" :timeout="0" v-if="isLoading" />
            </div>
            <VSkeleton :count="3" height="lg" :timeout="0" />
          </div>
          <div
            class="tw-ml-2 tw-flex tw-items-center tw-gap-1 tw-pb-2 tw-text-sm tw-text-grey-500"
            v-if="Object.keys(data || {}).length === 0 && term?.length && !isLoading"
          >
            Er zijn geen resultaten gevonden. Probeer een andere zoekterm.
          </div>
          <div
            class="tw-py-2"
            v-else-if="
              Object.keys(data || {}).length === 0 && !term && recenteZoekopdrachten.length
            "
          >
            <span class="tw-ml-2 tw-flex tw-items-center tw-gap-1 tw-text-sm tw-text-grey-500">
              Recent gezocht
            </span>
            <div
              @click="term = result"
              v-for="result in recenteZoekopdrachten"
              :key="result"
              class="tw-mt-2 tw-rounded tw-p-2 hover:tw-bg-grey-100"
            >
              <div
                class="tw-flex tw-flex-row tw-items-center tw-justify-between tw-gap-2 tw-text-grey-700 hover:tw-cursor-pointer"
              >
                <div class="tw-flex tw-items-center tw-gap-4">
                  <span class="tw-line-clamp-1 tw-break-all tw-font-bold"> {{ result }} </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </VPopover>
  </div>
</template>
